/**
 * Affiliates section — record labels, PR firms, and partners that work with
 * Digital Revolution Radio. Pure logo grid: each tile is a clickable link
 * that opens the affiliate's official Facebook / website in a new tab.
 *
 * Source list mirrors digitalrevolutionradio.com/affiliates.
 */
import { useState } from "react";
import { ExternalLink } from "lucide-react";

/**
 * Rough category tag inferred from the name so brutalist wordmark tiles
 * still convey what each partner does when a logo hasn't been rehosted yet.
 */
function categoryFor(name = "") {
  const n = name.toLowerCase();
  if (n.includes("pr") || n.includes("promotion") || n.includes("promotions") || n.includes("promo") || n.includes("media"))
    return "PR";
  if (n.includes("management") || n.includes("mgmt")) return "MGMT";
  return "LABEL";
}
const AFFILIATES = [
  { name: "Inverse Records", logo: "https://files.secure.website/wscfus/9318697/31233430/inverse-record-label-w926-o.jpg", url: "https://www.facebook.com/inverserecords" },
  { name: "Moonshine Doomshine Records & PR", logo: "https://files.secure.website/wscfus/9318697/31233441/mdrp-image-w750-o.png", url: "https://www.facebook.com/zachbdf.moonshine" },
  { name: "Eclipse Records", logo: "https://files.secure.website/wscfus/9318697/31233453/eclipse-w631-o.jpg", url: "https://www.facebook.com/eclipserecords" },
  { name: "The Metallist PR", logo: "https://files.secure.website/wscfus/9318697/31233457/the-metallist-w570-o.png", url: "https://www.facebook.com/themetallistpr" },
  { name: "Pure Steel Promotion", logo: "https://files.secure.website/wscfus/9318697/31233459/pure-steel-promotion-w200-o.jpg", url: "https://www.facebook.com/puresteelpromotion" },
  { name: "Perris Records", logo: "https://files.secure.website/wscfus/9318697/31233468/perris-records-w460-o.jpg", url: "https://www.facebook.com/PerrisRecords" },
  { name: "Imperative Music", logo: "https://files.secure.website/wscfus/9318697/31233469/imperative-w559-o.jpg", url: "https://www.facebook.com/imperativemusic.thelabel" },
  { name: "Asher Media Relations", logo: "https://files.secure.website/wscfus/9318697/31233471/asher-w750-o.jpg", url: "https://www.facebook.com/AsherMediaRelations" },
  { name: "Rockshot Records", logo: "https://files.secure.website/wscfus/9318697/31233484/rockshot-records-w750-o.jpg", url: "https://www.facebook.com/rockshotsrecords" },
  { name: "Nuclear Blast Records", logo: "https://files.secure.website/wscfus/9318697/31233634/nuclear-blast-w750-o.jpg", url: "https://www.facebook.com/nuclearblastrecords" },
  { name: "Curtain Call Records", logo: "https://files.secure.website/wscfus/9318697/31233699/curtain-call-records-w480-o.png", url: "https://www.facebook.com/CurtainCallRecords" },
  { name: "Vlad Promotion", logo: "https://files.secure.website/wscfus/9318697/31661198/vladpromotion2-w710-o.jpg", url: "https://www.facebook.com/vlad.nowajczykii" },
  { name: "Massacre Records", logo: "https://files.secure.website/wscfus/9318697/31859516/massacrelogoredblack-w750-o.jpg", url: "https://massacre-records.com/" },
  { name: "Red Dirt Records", logo: "https://files.secure.website/wscfus/9318697/32211262/red-dirt-records-w403.jpeg", url: "https://www.facebook.com/JasonHays21?mibextid=LQQJ4d" },
  { name: "Wanikiya Record", logo: "https://files.secure.website/wscfus/9318697/32278617/waniky-w599-o.jpg", url: "https://www.facebook.com/wanikiyarecord" },
  { name: "Paul Crosby Management", logo: "https://files.secure.website/wscfus/9318697/32280823/pcm-logo-on-black-background-w750-o.jpg", url: "https://www.facebook.com/PaulCrosbyManagement" },
  { name: "Wormholedeath", logo: "https://files.secure.website/wscfus/9318697/32291380/wormhole-w840-o.jpg", url: "https://wormholedeath.com/" },
  { name: "Reaction Management", logo: "https://files.secure.website/wscfus/9318697/32291384/reaction-management-logo-1-w500-o.jpg", url: "https://reaction-management.com/" },
  { name: "New Ocean Media", logo: "https://files.secure.website/wscfus/9318697/32291455/new-ocean-media.gif", url: "http://www.newoceanmedia.com/" },
  { name: "Angels PR Music Promotion", logo: "https://files.secure.website/wscfus/9318697/32307430/angels-pr-w750-o.jpg", url: "https://www.facebook.com/angelsprmusicpromotion/" },
  { name: "Sodeh Records", logo: "https://files.secure.website/wscfus/9318697/32503383/sodeh-w180-o.jpg", url: "https://www.sodeh.ca/" },
  { name: "Kick Down The Doors PR", logo: "https://files.secure.website/wscfus/9318697/32525063/logo-for-kick-down-w360-o.jpg", url: "https://kickdownthedoorspr.co.uk/" },
  { name: "Wrong Track Records", logo: "https://files.secure.website/wscfus/9318697/32525764/wrong-track-records-w750-o.jpg", url: "https://www.facebook.com/wrongtrackrecords/" },
  { name: "Old Crows Promotions", logo: "https://files.secure.website/wscfus/9318697/32617709/old-crows-promotions-w750-o.jpg", url: "https://www.facebook.com/oldcrowspromotions" },
  { name: "Hammerdown All In", logo: "https://files.secure.website/wscfus/9318697/32919401/hammerdown-logo-w500-o.png", url: "https://www.hammerdownallin.co.uk/" },
];

export default function Affiliates() {
  return (
    <section
      id="affiliates"
      className="relative border-t border-zinc-800 bg-zinc-950"
      data-testid="affiliates-section"
    >
      <div className="px-4 md:px-10 py-12 md:py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
              //_PARTNERS_AND_PROMOTERS
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-none text-zinc-50 glitch">
              OUR <span className="text-red-600">AFFILIATES</span>.
            </h2>
          </div>
          <div className="text-sm text-zinc-300 max-w-md">
            The labels, PR firms, and management teams we work with — pushing
            heavy music forward together. Click any logo to visit them.
          </div>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-0 border-l border-t border-zinc-800"
          data-testid="affiliates-grid"
        >
          {AFFILIATES.map((a) => (
            <AffiliateTile key={a.url} affiliate={a} />
          ))}
        </div>

        <p className="mt-8 text-xs text-zinc-600 tracking-widest uppercase">
          //_proud_partners · {AFFILIATES.length}_strong · always_growing
        </p>
      </div>
    </section>
  );
}

function AffiliateTile({ affiliate: a }) {
  const [imgFailed, setImgFailed] = useState(false);
  const category = categoryFor(a.name);
  const showImage = !!a.logo && !imgFailed;
  const testId = `affiliate-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      title={a.name}
      aria-label={a.name}
      className="relative border-r border-b border-zinc-800 bg-black hover:bg-zinc-900 group flex items-center justify-center aspect-square transition-colors hover:border-red-600 overflow-hidden"
      data-testid={testId}
    >
      {/* Category tag — top-left */}
      <span className="absolute top-2 left-2 text-[9px] tracking-[0.25em] uppercase font-mono text-red-500 bg-black/60 px-1.5 py-0.5 z-10">
        //_{category}
      </span>
      {/* External-link icon — bottom-right, appears on hover */}
      <ExternalLink className="absolute bottom-2 right-2 w-3 h-3 text-zinc-600 group-hover:text-red-500 transition-colors z-10" strokeWidth={2.2} />

      {showImage ? (
        <img
          src={a.logo}
          alt={a.name}
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="max-w-full max-h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        /* Brutalist wordmark fallback — displays the partner's name as a
           punchy typography card until the real logo is rehosted. */
        <div className="w-full h-full flex flex-col items-center justify-center px-4 py-6 text-center bg-gradient-to-br from-zinc-950 to-zinc-900">
          <div className="font-display leading-[0.95] tracking-tight text-zinc-50 group-hover:text-red-500 transition-colors text-lg md:text-xl lg:text-2xl break-words">
            {a.name}
          </div>
          <div className="mt-3 h-[2px] w-8 bg-red-600 group-hover:w-16 transition-all duration-300" />
        </div>
      )}
    </a>
  );
}
